import { useState, useEffect } from "react";
import { useStore } from "../../context/StoreContext";
import FollowTheSignsIcon from "@mui/icons-material/FollowTheSigns";
import { useSnackbar } from "notistack";
import { api } from "../../hooks/apiClient";
import { useSocket } from "../../hooks/useSocket";
import PhonelinkRingIcon from "@mui/icons-material/PhonelinkRing";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { CircularProgress } from "@mui/material";

const StoreSelector = ({ setUnread, onStoresLoaded }) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    selectedStore,
    updateSelectedStore,
    updateSelectedDestStore,
    isStoreSelected,
  } = useStore();

  // useSocket() can be null if this renders outside <SocketProvider>
  const {
    socket,
    status = "disconnected",
    error: socketError,
    retry,
  } = useSocket() || {};

  const [storesData, setStoresData] = useState([]);
  const [dStoresData, setDStoresData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStores = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("stores/active");
      setStoresData(response.data.source || []);
      setDStoresData(response.data.destination || []);
      onStoresLoaded?.(
        (response.data.source?.length || 0) +
          (response.data.destination?.length || 0),
      );
    } catch (err) {
      setError("Failed to load stores");
      console.error("Error fetching stores:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isStoreSelected) {
      fetchStores();
    }
  }, [selectedStore]);

  useEffect(() => {
    if (!socket) return;
    const handleCSNotification = (data) => {
      enqueueSnackbar(`CS Alert: ${data.message} from ${data.from}`, {
        variant: "success",
      });
      setUnread?.((prev) => prev + 1);
    };
    socket.on("cs_notification", handleCSNotification);
    return () => {
      socket.off("cs_notification", handleCSNotification);
    };
  }, [socket]);

  const handleStoreChange = (storeType, selectedId) => {
    const dataSource = storeType === "source" ? storesData : dStoresData;
    const picked = dataSource.find((s) => s.id.toString() === selectedId);

    if (picked) {
      updateSelectedStore({
        id: picked.id,
        public_name: picked.public_name,
        type: storeType,
        isActive: picked.isActive,
        phoneID: picked.sms_device,
      });
    }
    if (picked && storeType === "destination") {
      updateSelectedDestStore({
        id: picked.id,
        public_name: picked.public_name,
        type: storeType,
        isActive: picked.isActive,
      });
    }
  };

  const noStores =
    !loading && !error && storesData.length === 0 && dStoresData.length === 0;

  const socketIcon = {
    connected: (
      <span title="Live updates connected" className="text-success">
        <PhonelinkRingIcon />
      </span>
    ),
    connecting: <CircularProgress size={18} aria-label="Connecting" />,
    error: (
      <span
        title={socketError || "Connection problem"}
        className="text-danger d-inline-flex align-items-center gap-1"
      >
        <ErrorOutlineIcon />
        {retry && (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0"
            onClick={retry}
          >
            Retry
          </button>
        )}
      </span>
    ),
    disconnected: (
      <span title="Live updates off" className="text-muted">
        <PhonelinkEraseIcon />
      </span>
    ),
  }[status];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          {selectedStore && (
            <span className="badge bg-primary p-2">
              {selectedStore.public_name}
            </span>
          )}
          {socketIcon}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <FollowTheSignsIcon />

          <button
            onClick={fetchStores}
            className="btn btn-sm btn-outline-secondary"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          <select
            className="form-select form-select-sm w-auto"
            value={selectedStore?.type === "source" ? selectedStore.id : ""}
            onChange={(e) => handleStoreChange("source", e.target.value)}
            disabled={loading || storesData.length === 0}
          >
            <option value="">Select source store</option>
            {storesData.map((store) => (
              <option key={`source-${store.id}`} value={store.id}>
                {store.public_name}
              </option>
            ))}
          </select>

          <select
            className="form-select form-select-sm w-auto"
            value={
              selectedStore?.type === "destination" ? selectedStore.id : ""
            }
            onChange={(e) => handleStoreChange("destination", e.target.value)}
            disabled={loading || dStoresData.length === 0}
          >
            <option value="">Select destination store</option>
            {dStoresData.map((store) => (
              <option key={`dest-${store.id}`} value={store.id}>
                {store.public_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Messages sit under the bar, so the controls never disappear */}
      {loading && (
        <div className="small text-muted mt-1">Loading stores...</div>
      )}
      {error && (
        <div className="small text-danger mt-1">
          {error}. Press Refresh to try again.
        </div>
      )}
      {noStores && (
        <div className="small text-muted mt-1">
          No stores found for your account.
        </div>
      )}
      {status === "error" && socketError && (
        <div className="small text-danger mt-1">{socketError}</div>
      )}
    </div>
  );
};

export default StoreSelector;
